import { PenLine, Film, Share2, type LucideIcon } from 'lucide-react';

/**
 * THE PRODUCTION JOURNEY (RF3) — one phase at a time, not one control panel.
 *
 * The Studio grew into a single column holding the idea, length, look, scene flow, music, crew, cast, shots,
 * finishing and the final video — every control for every phase, visible at once. It is complete and it is
 * unusable: a creator cannot tell what to do next, and editing anything means hunting through everything.
 *
 * A production is a SEQUENCE, so the UI should be too. Each stage below is a page: it shows only its own
 * controls, states what it is for, and hands off to the next. The storyboard document is unchanged — this is
 * navigation over the same data, which is what makes it safe to introduce incrementally.
 *
 * These pages ARE the creation journey in PRODUCT.md ("The creation workflow"), mapped 1:1 so the product we
 * describe and the product we ship cannot drift apart:
 *
 *   Story   → Stage 1  Script (chat intake)
 *   Cast    → Stage 2  Casting: hire your actors, lock your objects
 *   Crew    → Stage 3  The Director & the Composer
 *   Shots   → Stage 4  Draft keyframes: the Canvas          (+ Stage 7 review — the SAME board once clips
 *                      exist; PRODUCT.md's own words are "hover the keyframe, the clip previews in place",
 *                      so splitting it would show the creator the same nodes on two pages)
 *   Generate→ Stage 5  The Generate moment — the cost + the decision to spend
 *   Publish → Stage 8  The final cut: watch, save, price
 *
 * WHAT IS DELIBERATELY NOT A PAGE, and why (owner decision 2026-08-12):
 * - **Stage 6, rendering progress.** A render takes 20-40 minutes, so it is a BACKGROUND JOB, not a place.
 *   Parking the creator on a progress page would build a waiting room — they should be casting the next
 *   episode while this one renders. Progress belongs in GLOBAL chrome that follows them across every stage
 *   (and across productions), which is also what the async-media design calls for: never a blocked spinner
 *   on the main action.
 * - **Stage 9, the crew rail** — runs alongside the whole journey; it is a pane, not a destination.
 * - **Stage 10, save/resume** — happens continuously and automatically. A "Saving" page has nothing to do on it.
 *
 * Stages are NOT hard-gated — a creator can jump back to change a line after locking shots — but each one
 * knows whether it is READY, so the journey can guide without trapping.
 */
/**
 * GENERATE IS NOT A STAGE (owner 2026-08-14): "Generate can be on the Canvas". Spending is a decision you
 * make while looking at the shots you are paying for, not on a page you navigate to afterwards — the board
 * already carries the Render panel and the cost-vs-balance bar. Publish stays its own page: it is a
 * different job (put it on your profile, price it), not a continuation of the edit.
 */
export type StageKey = 'project' | 'story' | 'cast' | 'director' | 'shots' | 'publish';   // cast/director remain addresses, not steps

export interface StageDef {
  key: StageKey;
  /** Short label for the stepper — a noun the creator recognises from filmmaking. */
  label: string;
  /** What this page is for, in one line. Shown as the page's own subtitle, so it never needs a manual. */
  purpose: string;
  Icon: LucideIcon;
}

/**
 * PROJECT IS NOT ONE OF THESE (owner 2026-08-24: *"when I highlight 'project' on Project Explorer, that's
 * where I can set the Project info, not per episode, because the Project info is inherited across to the
 * episodes"*).
 *
 * It briefly WAS a stage, and that was the wrong shape: a tab in the episode's journey implies the plot and
 * the world are things you set per episode, which is the exact confusion the two levels exist to end. The
 * project has its own surface, reached by selecting the project itself; the stages below are the EPISODE's.
 * `project` remains a valid `StageKey` because it is still an address (`/dashboard/{slug}/project`).
 *
 * CAST AND CREW LEFT TOO (owner 2026-08-24: *"cast, crew is also project level"*). They are facts about the
 * SHOW, not about one episode: the same faces and the same director carry every episode, which is the whole
 * promise of a series. Asking for them per episode invited a creator to re-cast episode 4 by accident and
 * made the journey four pages longer than the work actually is. They now live on the project surface beside
 * the plot and the world every episode inherits.
 *
 * What is left is genuinely per-episode: its script, its shots, and putting it out.
 */
export const STUDIO_STAGES: StageDef[] = [
  {
    key: 'story',
    label: 'Story',
    purpose: 'This episode: its title, and what happens in it.',
    Icon: PenLine,
  },
  {
    key: 'shots',
    label: 'Shots',
    purpose: 'The episode as a board — review each shot, then lock it.',
    Icon: Film,
  },
  {
    key: 'publish',
    label: 'Publish',
    purpose: 'Put it on your store — free, or priced for your audience.',
    Icon: Share2,
  },
];

/** Progress facts a stage can report, so the stepper can show what is done without gating the creator. */
export interface StageStatus {
  /** Enough is filled in for this stage to be considered handled. */
  done: boolean;
  /** A one-word summary shown under the label ("4 shots", "2 cast"), or null when there is nothing to say. */
  detail?: string | null;
}

/**
 * Derive each stage's status from the storyboard's own data — no separate progress state to drift out of sync.
 * Deliberately generous: a stage counts as done when the creator has given it anything real, because the point
 * is to orient them, not to grade them.
 */
export function stageStatuses(input: {
  /** How many of the project's required high-level facts are stated (plot, era, setting, characters). */
  projectFilled?: number;
  projectRequired?: number;
  idea: string;
  castCount: number;
  directorHired: boolean;
  shotCount: number;
  lockedCount: number;
  /** Kept in the input: `publish` and the resume decision still care whether a film exists. */
  hasFinalVideo: boolean;
  /** The finished film has been saved to the creator's public catalog (Stage 8). */
  published?: boolean;
}): Record<StageKey, StageStatus> {
  const { projectFilled = 0, projectRequired = 0, idea, castCount, directorHired, shotCount, lockedCount, hasFinalVideo, published } = input;
  void hasFinalVideo;   // read by callers deciding publish-readiness; no stage of its own any more
  return {
    // Done means the project has stated everything an episode needs to inherit. The detail counts rather than
    // grades — "2/4" tells a creator what is left without refusing to let them past it.
    project: {
      done: projectRequired > 0 && projectFilled >= projectRequired,
      detail: projectRequired ? `${projectFilled}/${projectRequired}` : null,
    },
    story: { done: !!idea.trim(), detail: idea.trim() ? 'written' : null },
    // Cast is genuinely optional (a product shot or a landscape needs none), so it never blocks — but when a
    // creator HAS cast someone, showing the count is the reassurance that the face is locked.
    cast: { done: castCount > 0, detail: castCount ? `${castCount} cast` : null },
    director: { done: directorHired, detail: directorHired ? 'hired' : null },
    shots: {
      done: shotCount > 0,
      detail: shotCount ? (lockedCount ? `${lockedCount}/${shotCount} locked` : `${shotCount} shots`) : null,
    },
    // Publishing is the point of the whole journey (PRODUCT.md: the finished film is the MIDDLE of the job),
    // so it is a stage of its own rather than a button hidden at the end of the render page.
    publish: { done: !!published, detail: published ? 'on your store' : null },
  };
}

/**
 * The stage a creator should land on when reopening — the first unfinished one, or the board if all done.
 *
 * Never `project`: opening an EPISODE puts you in that episode. The project's own surface is somewhere you go
 * deliberately, by selecting the project in the explorer.
 */
export function resumeStage(statuses: Record<StageKey, StageStatus>): StageKey {
  const next = STUDIO_STAGES.find((s) => !statuses[s.key].done);
  return next ? next.key : 'shots';
}

/**
 * IS THIS SHOT WRITTEN? — one rule, mirroring what the RENDER actually accepts.
 *
 * Owner 2026-09-13: *"we need to be able to generate preview keyframes"*. They could not, and this is why.
 * The server renders a shot that has a start frame OR composes to a non-empty prompt, and its composer reads
 * the shot's line AND the direction written on it. The client checked only the LINE — so a shot directed
 * entirely through the inspector, which is the biggest field on that panel, counted as nothing: the preview
 * strip never appeared, Generate stayed unavailable, the stage said no shots, and the footer told the
 * creator to "give a shot a line" as though they had written nothing at all.
 *
 * The client cannot call the server's composer, so it mirrors the same inputs. One rule in one place, because
 * three copies of "what counts as a shot" is exactly how they drifted apart in the first place.
 */
export function isWrittenShot(shot: { line?: string | null; freeText?: string | null; startFramePath?: string | null }): boolean {
  return Boolean(String(shot?.line || '').trim() || String(shot?.freeText || '').trim() || shot?.startFramePath);
}
